import { useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { useAdminBackupsStore } from '../../store/adminBackupsStore';
import { BackupCheckCard } from '../../components/admin';
import type { BackupEnv } from '../../types/admin';

const ENV_LABEL: Record<BackupEnv, string> = {
  production: 'Production',
  staging: 'Staging',
  unknown: 'Unknown environment',
};

function relative(iso: string | null): string {
  if (!iso) return '—';
  const diff = Math.max(0, Date.now() - Date.parse(iso));
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function isNotConfigured(error: string | null): boolean {
  return !!error && /HEALTHCHECKS_READ_API_KEY/.test(error);
}

export function BackupsTab() {
  const { data, lastSuccessfulFetchAt, loading, error, fetch, startPolling, stopPolling } =
    useAdminBackupsStore();

  useEffect(() => {
    void fetch();
    startPolling();
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NotConfigured — backend signaled 503 with the env-var message.
  if (error && isNotConfigured(error) && !data) {
    return (
      <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-sm text-gray-600 dark:text-gray-300">
        <div className="font-medium text-gray-900 dark:text-gray-100">Backups</div>
        <p className="mt-2">
          Healthchecks.io API key not configured — set <code className="px-1 rounded bg-gray-100 dark:bg-gray-700">HEALTHCHECKS_READ_API_KEY</code> on this environment.
        </p>
      </div>
    );
  }

  // Hard error, no prior data.
  if (error && !data) {
    return (
      <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="font-medium">Couldn't load backups</div>
        <div>{error}</div>
        <button className="mt-2 text-xs underline" onClick={() => void fetch()}>Retry</button>
      </div>
    );
  }

  // First paint, still loading.
  if (!data && loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>;
  }

  if (!data) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">No data yet.</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Backups</h1>
        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
          {error && lastSuccessfulFetchAt ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-2 py-0.5">
              Stale · last ok {relative(lastSuccessfulFetchAt)} · couldn't reach Healthchecks.io
            </span>
          ) : (
            <span>Last refreshed {relative(lastSuccessfulFetchAt)}</span>
          )}
          <button
            type="button"
            onClick={() => void fetch()}
            className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      {data.environments.map((group) => (
        <section key={group.env} className="space-y-3">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {ENV_LABEL[group.env]}
          </h2>
          {group.checks.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">No checks.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.checks.map((c) => (
                <BackupCheckCard key={c.slug} check={c} />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
